'use client'

import { motion } from 'framer-motion'
import { Volume2, VolumeX } from 'lucide-react'
import { useState, useRef, useEffect, memo } from 'react'
import { useTranslation } from '@/hooks/useTypedTranslation'
import { supabase } from '@/integrations/supabase/client'

const DEFAULT_VIDEO_URL = 'https://mojli.s3.us-east-2.amazonaws.com/Mojli+Website+upscaled+(12mb).webm'

// Memoized Hero component to prevent unnecessary re-renders
export const Hero = memo(function Hero() {
  const [isMuted, setIsMuted] = useState(true)
  const [videoUrl, setVideoUrl] = useState(DEFAULT_VIDEO_URL)
  const [isVideoLoaded, setIsVideoLoaded] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const { t } = useTranslation()

  // Fetch video URL from database configuration
  useEffect(() => {
    const fetchVideoUrl = async () => {
      const { data, error } = await supabase
        .from('site_config')
        .select('value')
        .eq('id', 'hero_video_url')
        .single()
      
      if (data?.value && !error) {
        setVideoUrl(data.value)
      }
    }
    fetchVideoUrl()
  }, [])

  // Ensure video is muted immediately on load to prevent any audio
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = 0
      videoRef.current.muted = true
      videoRef.current.defaultMuted = true
      
      // Mark video as loaded for LCP optimization
      videoRef.current.addEventListener('loadeddata', () => {
        setIsVideoLoaded(true)
      })

      // Force mute on play
      videoRef.current.addEventListener('play', () => {
        if (videoRef.current) {
          videoRef.current.muted = isMuted
          videoRef.current.volume = isMuted ? 0 : 0.7
        }
      })
      
      // Try to play the video
      const playPromise = videoRef.current.play()
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Autoplay failed, user interaction needed
        })
      }
    }
  }, [])

  // Update video mute state when isMuted changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted
      videoRef.current.volume = isMuted ? 0 : 0.7
    }
  }, [isMuted])

  return (
    <div className="relative h-screen w-full overflow-hidden bg-black">
      {/* Poster/Placeholder for LCP - shows immediately while video loads */}
      {!isVideoLoaded && (
        <div 
          className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800"
          aria-hidden="true"
        />
      )}

      {/* Video with explicit dimensions for CLS prevention */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover scale-110"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        // Poster helps with LCP
        poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1920 1080'%3E%3Crect fill='%23000'/%3E%3C/svg%3E"
        style={{ 
          opacity: isVideoLoaded ? 1 : 0,
          transition: 'opacity 0.5s ease-in-out'
        }}
      >
        <source src={videoUrl} type="video/webm" />
        Your browser does not support the video tag.
      </video>

      {/* Video Controls - 右上角 */}
      <div className="absolute top-24 right-6 sm:right-8 lg:right-12 z-40">
        <div className="flex items-center space-x-3">
          {/* Video Controls with Sound On indicator */}
          <div className="relative">
            <button
              onClick={() => setIsMuted(!isMuted)}
              className="glass-effect p-3 rounded-full text-white hover:bg-white/20 gentle-animation cursor-pointer"
              aria-label={isMuted ? t.hero.soundOn : 'Mute video'}
              aria-pressed={!isMuted}
            >
              {isMuted ? <VolumeX className="w-4 h-4" aria-hidden="true" /> : <Volume2 className="w-4 h-4" aria-hidden="true" />}
            </button>
            
            {/* Sound On indicator - only show when muted */}
            {isMuted && (
              <div className="absolute -bottom-10 right-0 flex items-center text-white/80">
                <span className="whitespace-nowrap font-medium text-sm mr-2">{t.hero.soundOn}</span>
                <span className="text-lg">↗</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Big Studio Title - Lower Left */}
      <motion.div
        initial={{ opacity: 1, x: 0 }}
        animate={{ opacity: 1, x: 0 }}
        className="absolute bottom-12 left-6 sm:left-8 lg:left-12 z-40"
      >
        <div className="max-w-2xl">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black leading-tight text-white">
            <span className="block">{t.hero.title1}</span>
            <span className="block">{t.hero.title2}</span>
          </h1>
          <p className="mt-4 text-lg sm:text-xl text-white/80">
            {t.hero.subtitle}
          </p>
        </div>
      </motion.div>
    </div>
  )
})
